import {
  type GeneratedAsset,
  SOCIAL_IMAGE_CREDIT_COST,
  type SocialImageOrientation,
} from '@pulsify/shared/dto'
import { Sparkles } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useGenerateImage } from '~/features/ai-studio/hooks'
import { AiProcessingOverlay } from '~/features/shared/components/ai-processing-overlay'
import {
  CreditCostNote,
  CreditCostTag,
  useCreditCost,
} from '~/features/shared/components/credit-cost'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { extensionFor, urlToFile } from '~/shared/lib/asset-image'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { Textarea } from '~/shared/ui/textarea'

export function AiImageButton({
  onUse,
  orientation,
  purpose,
  variant = 'ghost',
}: {
  onUse: (file: File) => void
  /** An event cover is generated without people. */
  purpose?: 'event-cover'
  /** Frame the image is generated in - pick by where it is headed, see the DTO. */
  orientation: SocialImageOrientation
  variant?: 'ghost' | 'outline'
}) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button type="button" variant={variant} size="sm" onClick={() => setOpen(true)}>
        <Sparkles className="h-4 w-4" />
        {m.aiImageButton()}
      </Button>
      <AiImageDialog
        open={open}
        onOpenChange={setOpen}
        onUse={onUse}
        orientation={orientation}
        purpose={purpose}
      />
    </>
  )
}

function AiImageDialog({
  open,
  onOpenChange,
  onUse,
  orientation,
  purpose,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUse: (file: File) => void
  orientation: SocialImageOrientation
  purpose?: 'event-cover'
}) {
  const { data: business } = useCurrentBusiness()
  const [prompt, setPrompt] = useState('')
  const [result, setResult] = useState<GeneratedAsset | null>(null)
  const promptId = useId()
  const generate = useGenerateImage(business?.id ?? '')
  const { state: cost } = useCreditCost(business?.id ?? '', SOCIAL_IMAGE_CREDIT_COST)
  const busy = generate.isPending

  const handleGenerate = () => {
    generate.mutate(
      { prompt, orientation, purpose },
      {
        onSuccess: ({ asset }) => setResult(asset),
        onError: (error) => toast.error(getErrorMessageFromError(error)),
      },
    )
  }

  const handleUse = async () => {
    if (!result) return
    try {
      onUse(await urlToFile(result.url, `ai-image.${extensionFor(result.mimeType)}`))
      onOpenChange(false)
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <>
      <AiProcessingOverlay open={busy} />
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!busy) onOpenChange(next)
        }}
      >
        <DialogContent showCloseButton={!busy}>
          <DialogHeader>
            <DialogTitle>{m.aiImageTitle()}</DialogTitle>
            <DialogDescription>{m.aiImageDescription()}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor={promptId}>{m.aiImagePromptLabel()}</Label>
            <Textarea
              id={promptId}
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={m.aiImagePromptPlaceholder()}
            />
          </div>

          {result && (
            <img
              src={result.url}
              alt=""
              className="max-h-64 w-full rounded-lg border object-contain"
            />
          )}

          <div className="flex items-end justify-between gap-2">
            <div className="flex flex-col gap-1">
              <Button
                type="button"
                variant="outline"
                onClick={handleGenerate}
                disabled={busy || !business || prompt.trim().length === 0 || cost === 'short'}
              >
                {busy ? <PulsifyLoader className="size-5" /> : <Sparkles className="h-4 w-4" />}
                {result ? m.aiRegenerate() : m.aiGenerate()}
                <CreditCostTag cost={SOCIAL_IMAGE_CREDIT_COST} />
              </Button>
              <CreditCostNote
                businessId={business?.id ?? ''}
                cost={SOCIAL_IMAGE_CREDIT_COST}
                className="text-xs"
              />
            </div>
            {result && (
              <Button type="button" onClick={handleUse}>
                {m.aiImageUse()}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
